import React from 'react';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';

// Dropdown — react-bootstrap-compatible compound API on top of Headless UI Menu.
//
// Placement (L6). The menu is anchored to its toggle with Headless UI's floating anchor, which
// flips above the toggle when there is no room below and keeps `padding` px clear of every
// viewport edge, so a menu opened from the last column no longer runs off the right edge.
// `align="end"` lines the menu's right edge up with the toggle's (kebab menus in a trailing
// actions column); the default lines up the left edges. The `align` prop used to be accepted
// and ignored.
//
// Dismissal. Headless UI closes the menu on Escape, on a click outside and on choosing an item.
// It also closes here when the page scrolls: an anchored menu otherwise follows its toggle up
// the screen and ends up drawn over the navbar.
type DivProps = React.HTMLAttributes<HTMLDivElement>;
type Align = 'start' | 'end';

const AlignCtx = React.createContext<Align>('start');

interface DropdownType extends React.FC<DivProps & { align?: Align }> {
  Toggle: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
      variant?: string;
      /**
       * Show the trailing caret. Default true for a labelled toggle; pass false for an
       * icon-only toggle (a kebab) whose icon already says "menu" - one control per menu, not a
       * kebab and a caret side by side.
       */
      caret?: boolean;
    }
  >;
  Menu: React.FC<DivProps>;
  Item: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { as?: React.ElementType; href?: string }>;
  Divider: React.FC;
}

/** Closes an open menu when anything other than the menu itself scrolls. */
const CloseOnScroll: React.FC<{ open: boolean; close: () => void }> = ({ open, close }) => {
  React.useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event) => {
      const target = e.target as Element | Document | null;
      if (target instanceof Element && target.closest('[role="menu"]')) return;
      close();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, close]);
  return null;
};

export const Dropdown = (({ className = '', align = 'start', children, ...rest }) => (
  <AlignCtx.Provider value={align}>
    <Menu as="div" className={`tw-relative tw-inline-block tw-text-left ${className}`} {...rest}>
      {({ open, close }) => (
        <>
          <CloseOnScroll open={open} close={close} />
          {children}
        </>
      )}
    </Menu>
  </AlignCtx.Provider>
)) as DropdownType;

Dropdown.Toggle = ({ className = '', caret = true, variant: _variant, children, ...rest }) => (
  <MenuButton
    className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-line tw-bg-surface tw-px-3 tw-py-1.5 tw-text-content hover:tw-bg-surface-hover ${className}`}
    {...rest}
  >
    {children}
    {caret && <i className="bi bi-caret-down-fill tw-text-xs" aria-hidden="true" />}
  </MenuButton>
);

Dropdown.Menu = ({ className = '', children, ...rest }) => {
  const align = React.useContext(AlignCtx);
  return (
    <MenuItems
      anchor={{ to: align === 'end' ? 'bottom end' : 'bottom start', gap: 4, padding: 8 }}
      className={`tw-z-[1060] tw-min-w-[10rem] tw-rounded tw-border tw-border-line tw-bg-surface tw-py-1 tw-shadow-lg focus:tw-outline-none ${className}`}
      {...rest}
    >
      {children}
    </MenuItems>
  );
};

Dropdown.Item = ({ className = '', as, href, children, ...rest }) => (
  <MenuItem>
    {() => {
      const Comp: React.ElementType = as || (href ? 'a' : 'button');
      return (
        <Comp
          href={href}
          className={`tw-block tw-w-full tw-cursor-pointer tw-px-4 tw-py-2 tw-text-left tw-text-content data-[focus]:tw-bg-surface-hover ${className}`}
          {...rest}
        >
          {children}
        </Comp>
      );
    }}
  </MenuItem>
);

Dropdown.Divider = () => <div className="tw-my-1 tw-border-t tw-border-line" />;

export default Dropdown;
